import React, { useState, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import SortableTable from '../SortableTable';

function TelematicTable({ data, startDate, endDate }) {
  const [sortBy, setSortBy] = useState('departure_datetime');
  const [sortDirection, setSortDirection] = useState('descending');

  // Définir les colonnes pour le tableau
  const columns = useMemo(() => [
    {
      key: 'departure_datetime',
      label: 'Départ',
      render: (item) => format(parseISO(item.departure_datetime), 'dd/MM/yyyy HH:mm', { locale: fr }),
      sortFunction: (a, b) => {
        return sortDirection === 'ascending'
          ? new Date(a.departure_datetime) - new Date(b.departure_datetime)
          : new Date(b.departure_datetime) - new Date(a.departure_datetime);
      }
    },
    {
      key: 'arrival_datetime',
      label: 'Arrivée',
      render: (item) => format(parseISO(item.arrival_datetime), 'dd/MM/yyyy HH:mm', { locale: fr }),
      sortFunction: (a, b) => {
        return sortDirection === 'ascending'
          ? new Date(a.arrival_datetime) - new Date(b.arrival_datetime)
          : new Date(b.arrival_datetime) - new Date(a.arrival_datetime);
      }
    },
    {
      key: 'driver_name',
      label: 'Conducteur',
      render: (item) => item.driver_name,
      sortFunction: (a, b) => {
        return sortDirection === 'ascending'
          ? a.driver_name.localeCompare(b.driver_name)
          : b.driver_name.localeCompare(a.driver_name);
      }
    },
    {
      key: 'car_registration_num',
      label: 'Immatriculation',
      render: (item) => item.car_registration_num,
      sortFunction: (a, b) => {
        return sortDirection === 'ascending'
          ? a.car_registration_num.localeCompare(b.car_registration_num)
          : b.car_registration_num.localeCompare(a.car_registration_num);
      }
    },
    {
      key: 'trip_time',
      label: 'Durée trajet',
      render: (item) => item.trip_time,
      sortFunction: (a, b) => {
        const [hoursA, minutesA] = a.trip_time.split(':').map(Number);
        const [hoursB, minutesB] = b.trip_time.split(':').map(Number);
        const totalMinutesA = hoursA * 60 + minutesA;
        const totalMinutesB = hoursB * 60 + minutesB;
        return sortDirection === 'ascending'
          ? totalMinutesA - totalMinutesB
          : totalMinutesB - totalMinutesA;
      }
    },
    {
      key: 'distance_traveled',
      label: 'Distance (km)',
      render: (item) => item.distance_traveled.toFixed(1),
      sortFunction: (a, b) => {
        return sortDirection === 'ascending'
          ? a.distance_traveled - b.distance_traveled
          : b.distance_traveled - a.distance_traveled;
      }
    },
    {
      key: 'avg_speed',
      label: 'Vitesse moyenne',
      render: (item) => item.avg_speed.toFixed(1),
      sortFunction: (a, b) => {
        return sortDirection === 'ascending'
          ? a.avg_speed - b.avg_speed
          : b.avg_speed - a.avg_speed;
      }
    },
    {
      key: 'fuel_consumption',
      label: 'Consommation (L)',
      render: (item) => item.fuel_consumption.toFixed(2),
      sortFunction: (a, b) => {
        return sortDirection === 'ascending'
          ? a.fuel_consumption - b.fuel_consumption
          : b.fuel_consumption - a.fuel_consumption;
      }
    },
    {
      key: 'eco_driving_score_percent',
      label: 'Score éco-conduite',
      render: (item) => `${item.eco_driving_score_percent}%`,
      sortFunction: (a, b) => {
        return sortDirection === 'ascending'
          ? a.eco_driving_score_percent - b.eco_driving_score_percent
          : b.eco_driving_score_percent - a.eco_driving_score_percent;
      }
    }
  ], [sortDirection]);

  // Trier les données
  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const column = columns.find(col => col.key === sortBy);
    if (!column) return data;

    return [...data].sort((a, b) => column.sortFunction(a, b));
  }, [data, sortBy, columns]);

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
          Détail des trajets
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Période: {format(startDate, 'dd/MM/yyyy')} - {format(endDate, 'dd/MM/yyyy')}
        </p>
      </div>

      {/* Tableau */}
      <SortableTable
        columns={columns}
        data={sortedData}
        sortBy={sortBy}
        onSortChange={setSortBy}
        sortDirection={sortDirection}
        onSortDirectionChange={setSortDirection}
        emptyMessage="Aucun trajet trouvé pour la période sélectionnée."
        countLabel={`Total: ${sortedData.length} trajets`}
      />
    </div>
  );
}

export default TelematicTable;